import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Upload, Download, Plus, Edit, Trash2, Search, Pencil, Check, X, Map, BarChart3, ShieldCheck, Clock, Eye, FileCode, Radio, History, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GTFS_FILES = [
  { key: 'agency', label: 'Agencias' },
  { key: 'routes', label: 'Rutas' },
  { key: 'stops', label: 'Paradas' },
  { key: 'trips', label: 'Viajes' },
  { key: 'stop_times', label: 'Horarios' },
  { key: 'calendar', label: 'Calendario' },
  { key: 'calendar_dates', label: 'Fechas Especiales' },
  { key: 'shapes', label: 'Formas' },
];

const ProjectEditorPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [currentTab, setCurrentTab] = useState('agency');
  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [formData, setFormData] = useState({});
  const [importUrlDialogOpen, setImportUrlDialogOpen] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null); // { percent, status }
  const [versionsDialogOpen, setVersionsDialogOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  useEffect(() => {
    fetchRecords(currentTab);
  }, [currentTab, projectId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = records.filter(record =>
        Object.values(record.data).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredRecords(filtered);
    } else {
      setFilteredRecords(records);
    }
  }, [searchTerm, records]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Error al cargar proyecto');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async (fileType) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/projects/${projectId}/records/${fileType}`);
      setRecords(response.data);
      setFilteredRecords(response.data);
    } catch (error) {
      setRecords([]);
      setFilteredRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditTitle = () => {
    setTitleValue(project?.name || '');
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!titleValue.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    try {
      await axios.put(`${API_URL}/projects/${projectId}`, { name: titleValue.trim() });
      setProject(prev => ({ ...prev, name: titleValue.trim() }));
      toast.success('Nombre actualizado');
    } catch (error) {
      toast.error('Error al actualizar nombre');
    }
    setEditingTitle(false);
  };

  const handleCancelEditTitle = () => {
    setEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') handleSaveTitle();
    if (e.key === 'Escape') handleCancelEditTitle();
  };

  const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB per chunk

  // Helper to connect to SSE and track processing progress
  const trackProcessingProgress = (taskId) => {
    return new Promise((resolve, reject) => {
      const evtSource = new EventSource(`${API_URL}/projects/${projectId}/upload/progress/${taskId}`);
      evtSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.status === 'not_found') {
            evtSource.close();
            reject(new Error('Tarea no encontrada'));
            return;
          }
          if (data.status === 'error') {
            evtSource.close();
            reject(new Error(data.error || 'Error procesando archivo'));
            return;
          }
          // Update progress
          const fileInfo = data.current_file ? ` (${data.current_file} - ${data.current_file_idx}/${data.total_files})` : '';
          const recordsInfo = data.records_inserted > 0 ? ` ${data.records_inserted.toLocaleString()} registros` : '';
          setUploadProgress({
            percent: data.percent,
            status: `Procesando${fileInfo}${recordsInfo}`,
          });
          if (data.status === 'done') {
            evtSource.close();
            resolve(data.stats);
          }
        } catch (e) {
          // ignore parse errors
        }
      };
      evtSource.onerror = () => {
        evtSource.close();
        // If SSE fails, resolve anyway (processing may have completed)
        resolve(null);
      };
    });
  };

  const handleUploadGTFS = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const totalSize = file.size;
    const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);

    // Small files (<4MB): use direct upload
    if (totalChunks <= 1) {
      try {
        setUploadProgress({ percent: 0, status: 'Subiendo archivo...' });
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/projects/${projectId}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000,
          onUploadProgress: (p) => {
            setUploadProgress({ percent: Math.round((p.loaded / p.total) * 30), status: 'Subiendo archivo...' });
          }
        });
        // Track processing via SSE
        const taskId = res.data.task_id;
        setUploadProgress({ percent: 35, status: 'Procesando archivo GTFS...' });
        await trackProcessingProgress(taskId);
        setUploadProgress({ percent: 100, status: 'Completado' });
        toast.success('Archivo GTFS subido exitosamente');
        fetchProject();
        fetchRecords(currentTab);
      } catch (error) {
        toast.error(error.response?.data?.detail || error.message || 'Error al subir archivo GTFS');
      } finally {
        setTimeout(() => setUploadProgress(null), 2000);
        e.target.value = '';
      }
      return;
    }

    // Large files: chunked upload
    try {
      setUploadProgress({ percent: 0, status: 'Iniciando subida por fragmentos...' });

      // 1. Init
      const initRes = await axios.post(`${API_URL}/projects/${projectId}/upload/init`, {
        filename: file.name,
        total_size: totalSize,
        total_chunks: totalChunks,
      });
      const uploadId = initRes.data.upload_id;

      // 2. Send chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalSize);
        const blob = file.slice(start, end);

        const chunkFd = new FormData();
        chunkFd.append('file', blob, `chunk_${i}`);

        await axios.post(
          `${API_URL}/projects/${projectId}/upload/chunk?upload_id=${uploadId}&chunk_index=${i}`,
          chunkFd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
        );

        const chunkPercent = Math.round(((i + 1) / totalChunks) * 40);
        setUploadProgress({ percent: chunkPercent, status: `Subiendo fragmento ${i + 1}/${totalChunks}...` });
      }

      // 3. Complete - triggers background processing
      setUploadProgress({ percent: 45, status: 'Iniciando procesamiento...' });
      const completeRes = await axios.post(
        `${API_URL}/projects/${projectId}/upload/complete?upload_id=${uploadId}`,
        {},
        { timeout: 30000 }
      );

      // 4. Track processing via SSE
      const taskId = completeRes.data.task_id;
      setUploadProgress({ percent: 50, status: 'Procesando archivo GTFS...' });
      await trackProcessingProgress(taskId);

      setUploadProgress({ percent: 100, status: 'Completado' });
      toast.success('Archivo GTFS subido exitosamente');
      fetchProject();
      fetchRecords(currentTab);
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Error al subir archivo GTFS');
    } finally {
      setTimeout(() => setUploadProgress(null), 2000);
      e.target.value = '';
    }
  };

  const handleImportFromUrl = async () => {
    if (!importUrl) {
      toast.error('Ingresa una URL válida');
      return;
    }
    try {
      setUploadProgress({ percent: 0, status: 'Descargando archivo desde URL...' });
      const res = await axios.post(`${API_URL}/projects/${projectId}/import-url?url=${encodeURIComponent(importUrl)}`, {}, { timeout: 600000 });
      const taskId = res.data.task_id;
      setUploadProgress({ percent: 10, status: 'Procesando archivo GTFS...' });
      await trackProcessingProgress(taskId);
      setUploadProgress({ percent: 100, status: 'Completado' });
      toast.success('Archivo GTFS importado exitosamente desde URL');
      fetchProject();
      fetchRecords(currentTab);
      setImportUrlDialogOpen(false);
      setImportUrl('');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Error al importar desde URL');
    } finally {
      setTimeout(() => setUploadProgress(null), 2000);
    }
  };

  // ============= VERSIONS =============
  const fetchVersions = async () => {
    setLoadingVersions(true);
    try {
      const res = await axios.get(`${API_URL}/projects/${projectId}/versions`);
      setVersions(res.data);
    } catch (error) {
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleOpenVersions = () => {
    setVersionsDialogOpen(true);
    fetchVersions();
  };

  const handleRestoreVersion = async (versionId, filename) => {
    if (!window.confirm(`¿Restaurar la versión "${filename}"? Esto reemplazará los datos actuales del proyecto.`)) return;
    try {
      setUploadProgress({ percent: 0, status: 'Restaurando versión...' });
      setVersionsDialogOpen(false);
      const res = await axios.post(`${API_URL}/projects/${projectId}/versions/${versionId}/restore`);
      const taskId = res.data.task_id;
      await trackProcessingProgress(taskId);
      setUploadProgress({ percent: 100, status: 'Restauración completada' });
      toast.success('Versión restaurada exitosamente');
      fetchProject();
      fetchRecords(currentTab);
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Error al restaurar versión');
    } finally {
      setTimeout(() => setUploadProgress(null), 2000);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm('¿Eliminar esta versión del historial? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API_URL}/projects/${projectId}/versions/${versionId}`);
      toast.success('Versión eliminada');
      fetchVersions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar versión');
    }
  };

  const handleExportGTFS = async () => {
    try {
      toast.info('Generando archivo GTFS...');
      const response = await axios.get(`${API_URL}/projects/${projectId}/export`, {
        responseType: 'blob',
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gtfs_${projectId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Archivo GTFS exportado');
    } catch (error) {
      toast.error('Error al exportar archivo GTFS');
    }
  };

  const handleExportNeTEx = async () => {
    try {
      toast.info('Generando archivo NeTEx...');
      const response = await axios.get(`${API_URL}/projects/${projectId}/export/netex`, {
        responseType: 'blob',
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${project?.name || 'netex'}_netex.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Archivo NeTEx exportado (Perfil España)');
    } catch (error) {
      toast.error('Error al exportar archivo NeTEx');
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setFormData(record.data);
    setEditDialogOpen(true);
  };

  const handleCreateRecord = () => {
    setEditingRecord(null);
    setFormData({});
    setEditDialogOpen(true);
  };

  const handleSaveRecord = async () => {
    try {
      if (editingRecord) {
        await axios.put(`${API_URL}/projects/${projectId}/records/${editingRecord.id}`, {
          data: formData
        });
        toast.success('Registro actualizado');
      } else {
        await axios.post(`${API_URL}/projects/${projectId}/records`, {
          file_type: currentTab,
          data: formData
        });
        toast.success('Registro creado');
      }
      setEditDialogOpen(false);
      fetchRecords(currentTab);
    } catch (error) {
      toast.error('Error al guardar registro');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('¿Estás seguro de eliminar este registro?')) return;
    try {
      await axios.delete(`${API_URL}/projects/${projectId}/records/${recordId}`);
      toast.success('Registro eliminado');
      fetchRecords(currentTab);
    } catch (error) {
      toast.error('Error al eliminar registro');
    }
  };

  if (loading && !project) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-slate-600">Cargando...</div>
      </div>
    );
  }

  const getFieldKeys = () => {
    if (filteredRecords.length === 0) return [];
    const allKeys = new Set();
    filteredRecords.forEach(record => {
      Object.keys(record.data).forEach(key => allKeys.add(key));
    });
    return Array.from(allKeys);
  };

  const fieldKeys = getFieldKeys();

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <Input
                  data-testid="edit-title-input"
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  className="text-2xl font-bold h-10 w-80"
                  autoFocus
                />
                <Button data-testid="save-title-btn" size="icon" variant="ghost" onClick={handleSaveTitle}>
                  <Check className="w-5 h-5 text-green-600" />
                </Button>
                <Button data-testid="cancel-title-btn" size="icon" variant="ghost" onClick={handleCancelEditTitle}>
                  <X className="w-5 h-5 text-slate-400" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <h1
                  className="text-2xl font-bold tracking-tight cursor-pointer hover:text-blue-600 transition-colors"
                  data-testid="editor-title"
                  onClick={handleStartEditTitle}
                >
                  {project?.name}
                </h1>
                <Button
                  data-testid="edit-title-btn"
                  size="icon"
                  variant="ghost"
                  onClick={handleStartEditTitle}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Pencil className="w-4 h-4 text-slate-400" />
                </Button>
              </div>
            )}
            <p className="text-sm text-slate-600 mt-0.5">{project?.description}</p>
          </div>
          <div className="flex gap-2">
            <label htmlFor="upload-input">
              <Button data-testid="upload-gtfs-button" variant="outline" onClick={() => document.getElementById('upload-input').click()}>
                <Upload className="w-4 h-4 mr-2" />
                Subir GTFS
              </Button>
            </label>
            <input id="upload-input" type="file" accept=".zip" onChange={handleUploadGTFS} className="hidden" />
            <Button data-testid="import-url-button" variant="outline" onClick={() => setImportUrlDialogOpen(true)}>
              <Download className="w-4 h-4 mr-2" />
              Importar URL
            </Button>
            <Button data-testid="export-gtfs-button" variant="outline" onClick={handleExportGTFS}>
              <Download className="w-4 h-4 mr-2" />
              Exportar GTFS
            </Button>
            <Button data-testid="export-netex-button" variant="outline" onClick={handleExportNeTEx} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <FileCode className="w-4 h-4 mr-2" />
              Exportar NeTEx
            </Button>
            <Button data-testid="versions-button" variant="outline" onClick={handleOpenVersions} className="border-purple-300 text-purple-700 hover:bg-purple-50">
              <History className="w-4 h-4 mr-2" />
              Versiones
            </Button>
          </div>
        </div>
      </div>

      {/* Project Navigation */}
      <div className="bg-white border-b px-6 py-2 flex gap-2">
        <Button
          data-testid="nav-visualizer"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/projects/${projectId}/visualizer`)}
          className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
        >
          <Eye className="w-4 h-4 mr-1" />
          Visualizador
        </Button>
        <Button
          data-testid="nav-map"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/projects/${projectId}/map`)}
        >
          <Map className="w-4 h-4 mr-1" />
          Mapa
        </Button>
        <Button
          data-testid="nav-validator"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/projects/${projectId}/validator`)}
        >
          <ShieldCheck className="w-4 h-4 mr-1" />
          Validador
        </Button>
        <Button
          data-testid="nav-stats"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/projects/${projectId}/stats`)}
        >
          <BarChart3 className="w-4 h-4 mr-1" />
          Estadísticas
        </Button>
        <Button
          data-testid="nav-history"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/projects/${projectId}/history`)}
        >
          <Clock className="w-4 h-4 mr-1" />
          Historial
        </Button>
        <Button
          data-testid="nav-gtfsrt"
          variant="outline"
          size="sm"
          onClick={() => navigate(`/projects/${projectId}/gtfs-rt`)}
          className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
        >
          <Radio className="w-4 h-4 mr-1" />
          GTFS-RT
        </Button>
      </div>

      {/* Upload Progress Bar */}
      {uploadProgress && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-4" data-testid="upload-progress">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="flex justify-between text-sm mb-1.5">
                <span className="font-medium text-blue-800">{uploadProgress.status}</span>
                <span className="font-mono text-blue-600 font-semibold">{uploadProgress.percent}%</span>
              </div>
              <div className="w-full bg-blue-100 rounded-full h-3 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${uploadProgress.percent}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={currentTab} onValueChange={setCurrentTab} className="h-full flex flex-col">
          <div className="bg-white border-b px-6">
            <TabsList className="h-12">
              {GTFS_FILES.map(file => (
                <TabsTrigger key={file.key} value={file.key} data-testid={`tab-${file.key}`} className="text-sm">
                  {file.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {GTFS_FILES.map(file => (
            <TabsContent key={file.key} value={file.key} className="flex-1 overflow-auto p-6 m-0">
              <div className="mb-4 flex items-center justify-between gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    data-testid="search-input"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button data-testid="add-record-button" onClick={handleCreateRecord}>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-8 text-slate-600">Cargando...</div>
              ) : filteredRecords.length === 0 ? (
                <Card className="p-8">
                  <p className="text-center text-slate-500">No hay registros. Sube un archivo GTFS o agrega uno manualmente.</p>
                </Card>
              ) : (
                <div className="border rounded-lg overflow-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        {fieldKeys.map(key => (
                          <th key={key} className="px-4 py-2 text-left text-xs font-medium text-slate-600 uppercase tracking-wider whitespace-nowrap">
                            {key}
                          </th>
                        ))}
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-600 uppercase tracking-wider whitespace-nowrap">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                      {filteredRecords.map(record => (
                        <tr key={record.id} className="hover:bg-slate-50" data-testid={`record-row-${record.id}`}>
                          {fieldKeys.map(key => (
                            <td key={key} className="px-4 py-2 text-xs font-mono whitespace-nowrap">
                              {record.data[key] || '-'}
                            </td>
                          ))}
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <Button data-testid={`edit-record-${record.id}`} variant="ghost" size="sm" onClick={() => handleEditRecord(record)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button data-testid={`delete-record-${record.id}`} variant="ghost" size="sm" onClick={() => handleDeleteRecord(record.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRecord ? 'Editar' : 'Crear'} Registro</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {(editingRecord ? Object.keys(editingRecord.data) : fieldKeys.length > 0 ? fieldKeys : ['id', 'name']).map(key => (
              <div key={key}>
                <Label htmlFor={`field-${key}`}>{key}</Label>
                <Input
                  id={`field-${key}`}
                  data-testid={`field-input-${key}`}
                  value={formData[key] || ''}
                  onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                  placeholder={key}
                />
              </div>
            ))}
            <div className="flex gap-2">
              <Button data-testid="save-record-button" onClick={handleSaveRecord} className="flex-1">Guardar</Button>
              <Button data-testid="cancel-record-button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import from URL Dialog */}
      <Dialog open={importUrlDialogOpen} onOpenChange={setImportUrlDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar GTFS desde URL</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="import-url">URL del archivo GTFS (ZIP)</Label>
              <Input
                id="import-url"
                data-testid="import-url-input"
                type="url"
                placeholder="https://ejemplo.com/gtfs.zip"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">Introduce la URL de un archivo ZIP GTFS público</p>
            </div>
            <div className="flex gap-2">
              <Button data-testid="import-url-submit-button" onClick={handleImportFromUrl} className="flex-1" disabled={!importUrl}>Importar</Button>
              <Button variant="outline" onClick={() => { setImportUrlDialogOpen(false); setImportUrl(''); }} className="flex-1">Cancelar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      <Dialog open={versionsDialogOpen} onOpenChange={setVersionsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Versiones GTFS
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 pr-2">
            {loadingVersions ? (
              <p className="text-center text-slate-500 py-4">Cargando versiones...</p>
            ) : versions.length === 0 ? (
              <p className="text-center text-slate-500 py-4">No hay versiones guardadas aún. Las versiones se crean automáticamente con cada importación.</p>
            ) : (
              versions.map((version) => (
                <Card key={version.id} className={`p-4 ${version.is_current ? 'border-blue-300 bg-blue-50' : ''}`} data-testid={`version-${version.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-sm truncate">{version.filename}</p>
                        {version.is_current && (
                          <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-medium">ACTIVA</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {new Date(version.uploaded_at).toLocaleString()} - {version.uploaded_by_name}
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {version.file_stats && Object.entries(version.file_stats).map(([file, count]) => (
                          <span key={file} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                            {file}: {count.toLocaleString()}
                          </span>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {(version.size_bytes / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {!version.is_current && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`restore-version-${version.id}`}
                            onClick={() => handleRestoreVersion(version.id, version.filename)}
                            className="text-xs"
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Restaurar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`delete-version-${version.id}`}
                            onClick={() => handleDeleteVersion(version.id)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectEditorPage;
